/**
 * Human-readable identifiers. Every record also has an internal UUID created on
 * the device; the human id is assigned by the server on first sync and never
 * changes or gets reused.
 */

export interface HumanIdParts {
  type: string;
  country: string;
  year: number;
  seq: number;
}

const TOKEN = /\{(TYPE|COUNTRY|YEAR|SEQ)(?::(\d+))?\}/g;

export function formatHumanId(format: string, parts: HumanIdParts): string {
  return format.replace(TOKEN, (_m, token: string, pad?: string) => {
    switch (token) {
      case 'TYPE':
        return parts.type.toUpperCase();
      case 'COUNTRY':
        return parts.country.toUpperCase();
      case 'YEAR':
        return String(parts.year);
      case 'SEQ':
        return String(parts.seq).padStart(pad ? Number(pad) : 0, '0');
      default:
        return _m;
    }
  });
}

/**
 * The sequence is kept per type, country and year (`survey_sequences`), so an
 * id is only unique when it carries all four: without {COUNTRY} two countries
 * both produce "SSS-2026-00001"; without {YEAR} the counter restarts each
 * January and repeats last year's ids.
 */
export function validateIdFormat(format: string): string[] {
  const errors: string[] = [];
  const tokens = new Set([...format.matchAll(TOKEN)].map((m) => m[1]));
  if (!tokens.has('SEQ')) errors.push('Format must contain {SEQ} so ids are unique.');
  for (const token of ['TYPE', 'COUNTRY', 'YEAR']) {
    if (!tokens.has(token)) errors.push(`Format must contain {${token}}: the sequence restarts per survey type, country and year.`);
  }
  const stripped = format.replace(TOKEN, '');
  if (/[{}]/.test(stripped)) errors.push('Unknown token in format.');
  if (/[^A-Za-z0-9\-_/.]/.test(stripped)) errors.push('Only letters, digits and - _ / . are allowed between tokens.');
  return errors;
}

export function findingHumanId(surveyHumanId: string, controlCode: string): string {
  return `${surveyHumanId}-${controlCode}`;
}

/** Short local reference shown on a draft before it has synced, e.g. "D-7F3A". */
export function draftReference(uuid: string): string {
  return `D-${uuid.replace(/-/g, '').slice(0, 4).toUpperCase()}`;
}
